import { pullAll, get } from 'lodash';

import store from 'APP_ROOT/store';
import updateGivenDataKeys from 'APP_ROOT/actions/update-given-data-keys';
import resetRepeaterKeys from 'APP_ROOT/actions/reset-repeater-keys';
import { getKeysFromSchema, isProtectedField } from 'APP_ROOT/utils/form';
import { protectedFields } from 'APP_ROOT/components/form-viewer/object-types/conditional';

/**
 * Builds the data mutations needed to clear every (non-protected) field inside
 * a tab whose conditions just turned it hidden. This mirrors the section/field
 * conditional clearing in ConditionalAssert.autoResetFieldValue (snapshot-level
 * clearing = mutating selectedForm.data), but for a top-level tab: tab fields
 * live flat in selectedForm.data, so there is no parentKey/parentIndex nesting.
 *
 * Only keys whose value is currently defined are returned, so re-running on an
 * already-cleared tab is a no-op (prevents update loops when a tab's own
 * condition depends on its fields).
 *
 * @param {Array} tabProperties - the hidden tab's `properties`
 * @param {Object} data - current selectedForm.data
 * @returns {{ resetKeys: Object, repeaters: Array<{key:string}> }}
 */
export const buildTabResetKeys = (tabProperties = [], data = {}) => {
  const { repeater, keys } = getKeysFromSchema(tabProperties);

  // Drop protected fields and ids, mirroring clearComplexObjecs.
  const clearableKeys = pullAll([...keys], [...protectedFields, 'id']).filter(
    key => !isProtectedField(protectedFields, key)
  );

  const resetKeys = clearableKeys.reduce((acc, key) => {
    const parsedKey = key.includes('.') ? key.split('.')[1] : key;
    if (get(data, parsedKey) !== undefined) {
      acc[parsedKey] = undefined;
    }
    return acc;
  }, {});

  const repeaters = repeater ? [{ key: repeater }] : [];

  return { resetKeys, repeaters };
};

/**
 * Clears the fields of a now-hidden tab at the snapshot level by dispatching the
 * same actions the section/field conditionals use. Reads current form data from
 * the store and dispatches only when there is something to clear.
 *
 * @param {Array} tabProperties - the hidden tab's `properties`
 */
const clearHiddenTabData = (tabProperties = []) => {
  const {
    form: { selectedForm = {} } = {},
  } = store.getState();
  const data = selectedForm.data || {};

  const { resetKeys, repeaters } = buildTabResetKeys(tabProperties, data);

  if (repeaters.length) {
    store.dispatch(resetRepeaterKeys(repeaters));
  }

  if (Object.keys(resetKeys).length) {
    store.dispatch(updateGivenDataKeys(resetKeys));
  }
};

export default clearHiddenTabData;
